import { api } from './api.js';

/**
 * Wake-up notifications on Android.
 *
 * Two paths: FCM inside the Capacitor app, Web Push in a browser and in an
 * iPhone home-screen web app. The Capacitor plugin is imported dynamically so
 * it never reaches the web bundle — browser users would otherwise download it
 * for a feature they cannot use.
 *
 * What this does NOT do, and cannot: show the message itself. Signal decrypts
 * natively while the app is closed and builds the notification from the real
 * content. Here decryption lives in JavaScript inside the WebView, and a
 * closed app runs no JavaScript. The notification therefore says only that
 * something arrived; the content appears once the app is open.
 *
 * Making that better means decrypting natively — which means a native client,
 * not a wrapper.
 */
/**
 * base64url in die Bytefolge, die `pushManager.subscribe` erwartet.
 *
 * Der Browser will ein Uint8Array, der Server liefert Text — dazwischen steht
 * diese Umrechnung. Sie ist der Grund, warum ein falsch kopierter VAPID-
 * Schluessel nicht mit einer Fehlermeldung auffaellt, sondern mit einem
 * Abonnement, das der Push-Dienst spaeter wortlos ablehnt.
 */
function decodeKey(base64url: string): Uint8Array<ArrayBuffer> {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));

  // Ueber einen eigenen ArrayBuffer statt Uint8Array.from: Nur so ist der
  // Typ eine BufferSource, die subscribe() annimmt. Ein Cast waere kuerzer
  // und wuerde denselben Fehler an anderer Stelle wieder ermoeglichen.
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/**
 * Web Push — Browser und iPhone-Web-Apps.
 *
 * Auf dem iPhone funktioniert das AUSSCHLIESSLICH, wenn die Seite ueber
 * "Zum Home-Bildschirm" installiert wurde. In einem Safari-Tab gibt es
 * `Notification` gar nicht; deshalb faellt der Aufruf dort still durch,
 * statt eine Berechtigung anzufragen, die niemand erteilen kann.
 */
async function setUpWebPush(sessionToken: string, vapidPublicKey: string): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  if (!('Notification' in window)) return;

  const registration = await navigator.serviceWorker.register('/sw.js');

  // Erst fragen, wenn der Server Push auch anbietet — eine Abfrage, die ins
  // Leere fuehrt, verbraucht die einzige Gelegenheit: Wer einmal ablehnt,
  // wird auf iOS nicht wieder gefragt.
  const permission =
    Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;

  if (permission !== 'granted') return;

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Ohne diese Zusage lehnen Chrome und Safari ab. Sie ist ehrlich: Jeder
      // Weckruf erzeugt eine sichtbare Benachrichtigung, siehe sw.js.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(vapidPublicKey),
    }));

  await api.registerPush(sessionToken, subscription.endpoint, 'web');
}

export async function setUpPush(sessionToken: string): Promise<void> {
  try {
    const available = await api.pushAvailable(sessionToken);

    // Im Browser: Web Push, sofern der Server einen VAPID-Schluessel hat.
    if (!('Capacitor' in window)) {
      if (available.webPush && available.vapidPublicKey) {
        await setUpWebPush(sessionToken, available.vapidPublicKey);
      }
      return;
    }

    if (!available.enabled) return;

    // Dynamisch geladen, damit Capacitor nicht im Web-Bundle landet: Jeder
    // Browser-Nutzer wuerde es sonst fuer eine Funktion herunterladen, die er
    // nicht benutzen kann.
    const { PushNotifications } = await import('@capacitor/push-notifications');

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return;

    // Der Token kommt asynchron ueber ein Ereignis, nicht als Rueckgabewert.
    await PushNotifications.addListener('registration', (info) => {
      void api.registerPush(sessionToken, info.value);
    });

    await PushNotifications.register();
  } catch {
    // Plugin nicht installiert oder Registrierung fehlgeschlagen. Push ist
    // Komfort — ohne ihn funktioniert die App unveraendert, sie meldet sich
    // nur nicht von selbst.
  }
}
